<template>
  <div v-if="componentReady" class="container-wrapper modifications-page">
    <BackLink
      :to="backRoute"
      :text="backButtonText"
    />

    <section class="container">
      <h1>{{ title }}</h1>

      <CarInfo
        :image="{ src: car.image, alt: car.year}"
        :year="car.year"
      />

      <div class="modifications-page__filters">
        <BaseSelect
          v-if="get(filters, 'fuelType.types.length')"
          v-model="currentFilter.fuelType"
          :placeholder="filters.fuelType.placeholder"
          :label="filters.fuelType.label"
          :items="filters.fuelType.types"
          class="modifications-page__filter"
        />
        <BaseSelect
          v-if="hasRealOptions(get(filters, 'volumeType.types')) && !currentFilter.batteryCapacity"
          v-model="currentFilter.volumeType"
          :placeholder="filters.volumeType.placeholder"
          :label="filters.volumeType.label"
          :items="filters.volumeType.types"
          class="modifications-page__filter"
          :disableAutoSelect="hasRealOptions(get(filters, 'batteryCapacity.types'))"
          :showArrow="hasRealOptions(get(filters, 'volumeType.types'))"
        />
        <BaseSelect
          v-if="hasRealOptions(get(filters, 'batteryCapacity.types')) && !currentFilter.volumeType"
          v-model="currentFilter.batteryCapacity"
          :placeholder="filters.batteryCapacity.placeholder"
          :label="filters.batteryCapacity.label"
          :items="filters.batteryCapacity.types"
          class="modifications-page__filter"
          :disableAutoSelect="hasRealOptions(get(filters, 'volumeType.types'))"
          :showArrow="hasRealOptions(get(filters, 'batteryCapacity.types')).length"
        />
        <BaseSelect
          v-if="get(filters, 'transmissionType.types.length') "
          v-model="currentFilter.transmissionType"
          :placeholder="filters.transmissionType.placeholder"
          :label="filters.transmissionType.label"
          :items="filters.transmissionType.types"
          class="modifications-page__filter"
        />
        <BaseSelect
          v-if="get(filters, 'driveType.types.length')"
          v-model="currentFilter.driveType"
          :placeholder="filters.driveType.placeholder"
          :label="filters.driveType.label"
          :items="filters.driveType.types"
          class="modifications-page__filter"
        />
        <BaseSelect
          v-if="get(filters, 'additionalOption.types.length', 0) > 1"
          v-model="currentFilter.additionalOption"
          :placeholder="filters.additionalOption.placeholder"
          :label="filters.additionalOption.label"
          :items="filters.additionalOption.types"
          class="modifications-page__filter"
        />
      </div>

      <div class="modifications-page__clear_all-wrapper" @click="clearFilters">
        <BaseClearButton>
          {{ clearText }}
        </BaseClearButton>
      </div>

      <div class="modifications-page__hr" />

      <h2>{{ filtrationResultTitle }}</h2>

      <transition-group
        tag="div"
        name="fade"
        class="modifications-page__modifications"
      >
        <div
          v-for="(mod, index) in modifications"
          :key="`modification-link-${index}`"
          class="modifications-page__modification"
        >
          <router-link v-if="getModel && getYear" :to="getLinkToNextStep(mod)">
            {{ mod.text }}
          </router-link>
        </div>
      </transition-group>
    </section>
  </div>
</template>

<script>
  // Step 3

  import { debounce, get } from 'lodash'
  import { mapGetters, mapActions } from 'vuex'
  import BackLink from '@/components/BackLink'
  import CarInfo from '@/components/CarInfo'
  import { DEBOUNCE_REQUESTS, DEFAULT_LANG } from '@/helpers/constant'

  export default {
    name: 'ModificationPage',
    components: {
      BackLink,
      CarInfo
    },
    data () {
      return {
        title: '',
        subtitle: '',
        clearText: '',
        subtitleNotFound: '',
        backButtonText: '',
        car: null,
        filters: null,
        modifications: [],
        componentReady: false,
        currentFilter: {
          fuelType: null,
          transmissionType: null,
          driveType: null,
          batteryCapacity: null,
          volumeType: null,
          additionalOption: null
        }
      }
    },
    computed: {
      ...mapGetters(['getModel', 'getYear']),
      backRoute () {
        const lang = this.$store.state.currentLang
        const query = lang !== DEFAULT_LANG ? { lang } : {}

        return {
          name: 'year',
          params: {
            model: this.getModel
          },
          query
        }
      },
      filtrationResultTitle () {
        return this.modifications.length ? this.subtitle : this.subtitleNotFound
      }
    },
    watch: {
      $route (to, from) {
        if (this.componentReady && (from.name === 'year' && to.name === 'modification')) {
          this.modelTextState()
          this.modelIdState()
          this.pushDataToDataLayer({
            page: {
              category: 'tools',
              subCategory: 'maintenance',
              sectionlv1: 'tool',
              sectionlv2: 'maintenance-calculator',
              language: this.$store.state.currentLang
            },
            event: {
              name: 'workflowevent',
              componentName: 'maintenance-calculator',
              componentGroup: 'tools',
              action: 'workflow-step',
              label: 'car-details'
            },
            workflow: {
              name: 'maintenance-calculator',
              step: 'car-details',
              conversion: '0'
            },
            product: [{
              category: 'cars',
              subCategory: 'new-cars',
              productInfo: {
                productId: this.modelId,
                productName: this.modelText,
                tmeModelId: this.modelId,
                modelName: this.modelText
              }
            }]
          }, true)
        }
        if (from.name === 'tech-map' && to.name === 'modification') {
          this.modelTextState()
          this.modelIdState()
          this.pushDataToDataLayer({
            page: {
              category: 'tools',
              subCategory: 'maintenance',
              sectionlv1: 'tool',
              sectionlv2: 'maintenance-calculator',
              language: this.$store.state.currentLang
            },
            event: {
              name: 'workflowevent',
              componentName: 'maintenance-calculator',
              componentGroup: 'tools',
              action: 'workflow-step',
              label: 'select-production-year'
            },
            workflow: {
              name: 'maintenance-calculator',
              step: 'select-production-year',
              conversion: '0'
            },
            product: [{
              category: 'cars',
              subCategory: 'new-cars',
              productInfo: {
                productId: this.modelId,
                productName: this.modelText,
                tmeModelId: this.modelId,
                modelName: this.modelText
              }
            }]
          }, true)
        }
      },
      componentReady (v) {
        if (v) {
          this.modelTextState()
          this.modelIdState()
          this.pushDataToDataLayer({
            page: {
              category: 'tools',
              subCategory: 'maintenance',
              sectionlv1: 'tool',
              sectionlv2: 'maintenance-calculator',
              language: this.$store.state.currentLang
            },
            event: {
              name: 'workflowevent',
              componentName: 'maintenance-calculator',
              componentGroup: 'tools',
              action: 'workflow-step',
              label: 'car-details'
            },
            workflow: {
              name: 'maintenance-calculator',
              step: 'car-details',
              conversion: '0'
            },
            product: [{
              category: 'cars',
              subCategory: 'new-cars',
              productInfo: {
                productId: this.modelId,
                productName: this.modelText,
                tmeModelId: this.modelId,
                modelName: this.modelText
              }
            }]
          }, true)
        }
      }
    },
    async created () {
      await this.getPageData({ year: this.getYear })

      const debouncedGetData = debounce(() => {
        this.getPageData(this.buildFiltersPayload())
      }, DEBOUNCE_REQUESTS)

      Object.keys(this.currentFilter).forEach(filterKey => {
        this.$watch(`currentFilter.${filterKey}`, () => {
          debouncedGetData()
        })
      })

      this.componentReady = true
    },
    methods: {
      get,
      ...mapActions(['fetchModificationPageData']),
      getLinkToNextStep (modification) {
        let query = {}
        if (this.$store.state.currentLang !== DEFAULT_LANG) {
          query = { lang: this.$store.state.currentLang }
        }
        return {
          name: 'tech-map',
          params: {
            modification: modification.value
          },
          query
        }
      },
      async getPageData ({ year, transmission, drive, fuel, volume, batteryCapacity, additional }) {
        const data = await this.fetchModificationPageData({
          year, transmission, drive, fuel, volume, additional, batteryCapacity
        })

        this._data = Object.assign(this._data, data)
      },
      hasRealOptions (types) {
        return Array.isArray(types) &&
          types.some(item => item.value !== null)
      },
      buildFiltersPayload () {
        const {
          transmissionType,
          driveType,
          fuelType,
          volumeType,
          batteryCapacity,
          additionalOption
        } = this.currentFilter

        console.log(batteryCapacity)
        return {
          year: this.getYear,
          transmission: transmissionType,
          drive: driveType,
          fuel: fuelType,
          additional: additionalOption,
          volume: volumeType,
          batteryCapacity: batteryCapacity
        }
      },
      clearFilters () {
        Object.assign(this.currentFilter, {
          fuelType: null,
          transmissionType: null,
          driveType: null,
          volumeType: null,
          additionalOption: null,
          batteryCapacity: null
        })
      },

      modelTextState () {
        this.modelText = get(this, '$store.state.model.modelText', '') || get(this, '_data.car.toyotaModelName', '')
      },
      modelIdState () {
        this.modelId = get(this, '$store.state.model.modelId', '') || get(this, '_data.car.toyotaModelId', '')
      },
      pushDataToDataLayer (params = {} || window.tngaDataLayer, pushDataLayer = false) {
        window.dataLayer = window.dataLayer || []
        window.tngaDataLayer = { ...params }

        if (pushDataLayer) window.dataLayer.push({ 'event': 'workflowevent' })
      }
    }
  }
</script>

<style lang="scss">
.modifications-page {
  &__filters {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    @include mobile {
      flex-direction: column;
    }
  }

  &__filter {
    width: 335px;

    @include mobile {
      width: 100%;
      margin-bottom: 5px;
    }
  }

  &__hr {
    width: 100%;
    height: 1px;
    margin: 5px auto 35px auto;
    background-color: $c-gray-medium;

    @include mobile {
      margin: 15px auto;
    }
  }

  &__modifications {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding-bottom: 50px;
  }

  &__modification {
    display: inline-block;
    width: 335px;
    margin-bottom: 20px;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;

    &:last-child {
      margin-bottom: 0;
    }

    @include mobile {
      width: 100%;
    }
  }

  &__clear_all-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 25px;
  }
}
</style>
